import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Check, X, MessageCircle, Sparkles, BookOpen, RefreshCw, BarChart3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Badge, EmptyState, Card } from "@/components/ui-em";

export default function AdminAIReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [filter, setFilter] = useState("open");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/ai-review-queue", { params: { status: filter } }).then((r) => r.data),
      api.get("/admin/ai/stats").then((r) => r.data).catch(() => null),
    ]).then(([q, s]) => {
      setItems(q);
      setStats(s);
    }).finally(() => setLoading(false));
  };

  useEffect(load, [filter]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/ai-review-queue/${id}/resolve`);
      toast.success("Marked resolved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this question?")) return;
    try {
      await api.delete(`/admin/ai-review-queue/${id}`);
      toast.success("Dismissed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reindex = async () => {
    if (!window.confirm("Re-index all published content? This may take a minute.")) return;
    setReindexing(true);
    try {
      const { data } = await api.post("/admin/ai/reindex");
      toast.success(`Re-indexed ${data.total_chunks} chunks across ${Object.values(data.counts).reduce((a, b) => a + b, 0)} sources`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReindexing(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · EM AI"
        title="AI Review Queue"
        description="Questions EM AI couldn't answer confidently. Fill the gaps in the Knowledge Base — EM AI improves automatically."
        actions={<Button icon={RefreshCw} onClick={reindex} loading={reindexing} variant="secondary" size="lg">Re-index all content</Button>}
      />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Chunks</span>
              <BarChart3 className="w-3.5 h-3.5 text-gold" />
            </div>
            <div className="font-display text-3xl font-light">{stats.total_chunks}</div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Conversations</span>
              <MessageCircle className="w-3.5 h-3.5 text-gold" />
            </div>
            <div className="font-display text-3xl font-light">{stats.conversations}</div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Open reviews</span>
              <AlertCircle className="w-3.5 h-3.5 text-gold" />
            </div>
            <div className="font-display text-3xl font-light">{stats.open_reviews}</div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Articles indexed</span>
              <BookOpen className="w-3.5 h-3.5 text-gold" />
            </div>
            <div className="font-display text-3xl font-light">{stats.by_type?.article?.sources || 0}</div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {["open", "resolved", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-lg text-xs font-medium uppercase tracking-widest ${
              filter === s ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10"
            }`}
            style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={filter === "open" ? "No open reviews" : "No items in this queue"}
          description="When EM AI can't answer confidently, or a user marks an answer unhelpful, questions land here for you to review."
        />
      ) : (
        <div className="space-y-3">
          {items.map((q) => (
            <Card key={q.id} className="p-6">
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={q.reason === "negative_feedback" ? "danger" : "warn"} size="sm" uppercase>
                    {q.reason === "negative_feedback" ? "Marked unhelpful" : "No context"}
                  </Badge>
                  <Badge variant={q.status === "open" ? "outline" : "success"} size="sm" uppercase>{q.status}</Badge>
                  <span className="text-[11px] text-zinc-500">
                    {q.user_name} · {new Date(q.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {q.status === "open" && (
                    <button
                      onClick={() => resolve(q.id)}
                      className="p-2 rounded-md text-zinc-500 hover:text-emerald-300 hover:bg-emerald-500/10"
                      style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                      title="Mark resolved"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => dismiss(q.id)}
                    className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                    style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-display text-lg text-white mb-2">{q.question}</h3>
              {q.user_comment && (
                <p className="text-sm text-zinc-400 leading-relaxed p-3 rounded-lg bg-white/[0.02] border-l-2 border-red-500/30 mb-3">
                  <span className="text-[10px] uppercase tracking-widest text-red-300 font-semibold block mb-1">User comment</span>
                  {q.user_comment}
                </p>
              )}
              <div className="mt-4 flex items-center gap-2">
                <Button as={Link} to="/knowledge-base/new" size="sm" icon={BookOpen} variant="gold_outline">
                  Create article
                </Button>
                {q.status === "open" && (
                  <Button size="sm" variant="secondary" onClick={() => resolve(q.id)}>
                    Mark resolved
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
